/* eslint-disable max-depth */
import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';

import {
  GANTT_PADDING_ABOVE,
  GANTT_SECTION_PADDING,
} from '@/features/canvas/constants/swimlane-constants';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';

import { DependencyModeState } from '../../hooks/useDependencyMode';
import { getInitiativeDates } from '../../utils/getInitiativeDates';

// Variable heights for parent vs child rows (matching GanttTimelineSection)
const GANTT_PARENT_HEIGHT = 28; // Height for parent rows
const GANTT_CHILD_HEIGHT = 20; // Height for child rows (more compact)

// Helper function to get row height based on depth (matching GanttTimelineSection)
const getRowHeight = (depth: number) => (depth > 0 ? GANTT_CHILD_HEIGHT : GANTT_PARENT_HEIGHT);

interface GanttDependencyArrowsProps {
  dependencyState: DependencyModeState;
  // Enhanced initiatives list including injected children
  enhancedInitiatives: FilteredNodes[];
  getSectionHeight: (sectionId: number) => number;
  // Use the same data structure as GanttTimelineSection to ensure consistent depth and ordering
  initiativesBySection: Record<number, { depth: number; node: FilteredNodes }[]>;
  timelineEnd: dayjs.Dayjs;
  timelineStart: dayjs.Dayjs;
  zoomMultiplier: number;
  // Gantt-specific isolation props
  connectedConnectionIds?: Set<string>;
  isIsolationActive?: boolean;
}

interface Arrow {
  from: { nodeId: number; x: number; y: number };
  key: string;
  opacity: number;
  to: { nodeId: number; x: number; y: number };
}

const GanttDependencyArrows: React.FC<GanttDependencyArrowsProps> = ({
  dependencyState,
  timelineStart,
  timelineEnd,
  getSectionHeight,
  zoomMultiplier,
  initiativesBySection,
  connectedConnectionIds = new Set(),
  isIsolationActive = false,
  enhancedInitiatives,
}) => {
  const {
    activeFilterIds,
    optionCheckedIds,
    filters,
    sortedVerticalSections,
    sortedHorizontalSections,
  } = useEditorContext();

  const arrows = useMemo(() => {
    if (!dependencyState.isDependencyMode) {
      return [];
    }

    const arrowsToRender: Arrow[] = [];

    // Combine sections exactly like GanttTimeline does
    const allSections = [...sortedVerticalSections, ...sortedHorizontalSections];

    // Use the provided initiativesBySection data - this ensures consistent depth and ordering
    // with GanttTimelineSection (no independent depth calculation that could get it wrong)
    const sectionOffsets = new Map<number, number>();

    // Calculate cumulative Y offsets for each section using the same logic as rendered sections
    let cumulativeOffset = 0;
    allSections.forEach(section => {
      if (section.id !== undefined) {
        sectionOffsets.set(section.id, cumulativeOffset);
        const sectionInitiatives = initiativesBySection[section.id] || [];
        const calculatedHeight =
          sectionInitiatives.length > 0
            ? sectionInitiatives.reduce((sum, item) => sum + getRowHeight(item.depth), 0) +
              GANTT_SECTION_PADDING
            : 97.06; // Use updated minimum height
        // Use the sidebar's calculated height, or fall back to our calculation
        const sidebarHeight = getSectionHeight(section.id);
        const sectionHeight = Math.max(calculatedHeight, sidebarHeight, 97.06);
        cumulativeOffset += sectionHeight;
      }
    });

    // Helper to get the actual Y position of an initiative and its opacity state
    const getInitiativePosition = (node: FilteredNodes) => {
      const { sectionId } = node;
      if (sectionId === undefined) return null;

      const sectionNodes = initiativesBySection[sectionId] || [];
      let indexInSection = sectionNodes.findIndex(item => item.node.id === node.id);

      // If not found in this section directly, search all sections (for child nodes)
      if (indexInSection === -1) {
        const foundResult = Object.entries(initiativesBySection)
          .map(([sectId, sectNodes]) => {
            const foundIndex = sectNodes.findIndex(item => item.node.id === node.id);
            return foundIndex >= 0 ? { sectId: Number(sectId), foundIndex, sectNodes } : null;
          })
          .find(result => result !== null);

        if (foundResult) {
          const { sectId: foundSectionId, foundIndex, sectNodes: foundSectionNodes } = foundResult;
          indexInSection = foundIndex;

          // Recalculate with the correct section offset
          const foundSectionOffset = sectionOffsets.get(foundSectionId) || 0;
          const barTopPosition =
            GANTT_PADDING_ABOVE +
            foundSectionNodes
              .slice(0, indexInSection)
              .reduce((sum, item) => sum + getRowHeight(item.depth), 0);

          const currentItem = foundSectionNodes[indexInSection];
          const rowHeight = getRowHeight(currentItem.depth);
          const actualBarHeight = currentItem.depth > 0 ? 10 : 16;
          const barCenterInRow = (rowHeight - actualBarHeight) / 2 + actualBarHeight / 2;
          const yPosition = foundSectionOffset + barTopPosition + barCenterInRow;

          const { startDate, endDate } = getInitiativeDates(node.initiatives);
          if (!startDate || !endDate) {
            return null;
          }

          const startDateObj = dayjs(startDate);
          const endDateObj = dayjs(endDate);
          const totalDays = timelineEnd.diff(timelineStart, 'days');

          const daysSinceStart = startDateObj.diff(timelineStart, 'days');
          const durationDays = endDateObj.diff(startDateObj, 'days') + 1;

          const leftPercent = Math.max(0, (daysSinceStart / totalDays) * 100);
          const rightPercent = Math.min(100, leftPercent + (durationDays / totalDays) * 100);

          return {
            start: { x: leftPercent, y: yPosition },
            end: { x: rightPercent, y: yPosition },
          };
        }

        return null; // Not found in any section
      }

      const sectionOffset = sectionOffsets.get(sectionId) || 0;
      // Match GanttTimelineSection's calculation exactly using variable heights:
      // Calculate cumulative position for this item by summing heights of all previous rows
      let barTopPosition = GANTT_PADDING_ABOVE;
      for (let i = 0; i < indexInSection; i += 1) {
        barTopPosition += getRowHeight(sectionNodes[i].depth);
      }
      // Calculate the center of the actual bar, not the row
      const currentItem = sectionNodes[indexInSection];
      const rowHeight = getRowHeight(currentItem.depth);
      const actualBarHeight = currentItem.depth > 0 ? 10 : 16; // Actual bar heights from InitiativeBar
      // Bar is centered in row: topPosition + (rowHeight - barHeight) / 2
      // Center of bar is: topPosition + (rowHeight - barHeight) / 2 + barHeight / 2
      // Which simplifies to: topPosition + rowHeight / 2
      // But since actual bar height != row height, we need:
      const barCenterInRow = (rowHeight - actualBarHeight) / 2 + actualBarHeight / 2;
      const yPosition = sectionOffset + barTopPosition + barCenterInRow;

      const { startDate, endDate } = getInitiativeDates(node.initiatives);
      if (!startDate || !endDate) {
        return null;
      }

      const startDateObj = dayjs(startDate);
      const endDateObj = dayjs(endDate);
      const totalDays = timelineEnd.diff(timelineStart, 'days');

      const daysSinceStart = startDateObj.diff(timelineStart, 'days');
      const durationDays = endDateObj.diff(startDateObj, 'days') + 1;

      const leftPercent = Math.max(0, (daysSinceStart / totalDays) * 100);
      const rightPercent = Math.min(100, leftPercent + (durationDays / totalDays) * 100);

      return {
        start: { x: leftPercent, y: yPosition },
        end: { x: rightPercent, y: yPosition },
      };
    };

    // Helper to check if an initiative is greyed out due to filters (matches CanvasNodes logic)
    const getInitiativeOpacity = (node: FilteredNodes) => {
      const activeFilters =
        filters?.filter(f => f.id !== undefined && activeFilterIds.includes(f.id!)) || [];

      const doesNodeHaveActiveFilter = activeFilters.some(
        filter => filter.nodes?.some(n => n.nodeId === node.id) ?? false
      );

      const isNodesOptionChecked = activeFilters.every(filter =>
        filter.selection === 'multiple'
          ? filter.nodes?.some(
              n => n.nodeId === node.id && optionCheckedIds.includes(n.optionId ?? -1)
            ) ?? false
          : optionCheckedIds.includes(filter.nodes?.find(n => n.nodeId === node.id)?.optionId ?? -1)
      );

      const isNotInFilter = Boolean(
        (doesNodeHaveActiveFilter && optionCheckedIds.length > 0 && !isNodesOptionChecked) ||
          (!doesNodeHaveActiveFilter && activeFilterIds.length > 0)
      );

      const isGreyDueToFilter = activeFilterIds.length > 0 && isNotInFilter;
      return isGreyDueToFilter ? 0.15 : 1; // Normal opacity
    };

    // Collect ALL dependencies from all initiatives (including hidden children)
    const allDependencies: { masterNodeId: number; slaveNodeId: number }[] = [];
    enhancedInitiatives.forEach(node => {
      if (node.dependencies) {
        allDependencies.push(...node.dependencies);
      }
    });

    // Process each dependency
    allDependencies.forEach(dep => {
      // Find source and target nodes from all enhanced initiatives (including hidden children)
      const sourceNode = enhancedInitiatives.find(
        n => n.id === dep.masterNodeId || n.initiatives?.id === dep.masterNodeId
      );
      const targetNode = enhancedInitiatives.find(
        n => n.id === dep.slaveNodeId || n.initiatives?.id === dep.slaveNodeId
      );

      if (!sourceNode || !targetNode) {
        return;
      }

      const sourcePos = getInitiativePosition(sourceNode);
      const targetPos = getInitiativePosition(targetNode);

      if (!sourcePos || !targetPos) {
        return;
      }

      // Calculate arrow opacity based on source and target node opacity
      const sourceOpacity = getInitiativeOpacity(sourceNode);
      const targetOpacity = getInitiativeOpacity(targetNode);
      // If either source or target is greyed out, grey out the arrow
      let arrowOpacity = Math.min(sourceOpacity, targetOpacity);

      // Apply Gantt isolation filtering - only show connected arrows when isolation is active
      const connectionKey = `${dep.masterNodeId}-${dep.slaveNodeId}`;
      if (isIsolationActive && !connectedConnectionIds.has(connectionKey)) {
        arrowOpacity = 0.1; // Dim non-connected arrows significantly
      }

      arrowsToRender.push({
        from: {
          x: sourcePos.start.x,
          y: sourcePos.start.y,
          nodeId: dep.slaveNodeId,
        },
        to: {
          x: targetPos.start.x,
          y: targetPos.start.y,
          nodeId: dep.masterNodeId,
        },
        key: connectionKey,
        opacity: arrowOpacity,
      });
    });

    return arrowsToRender;
  }, [
    dependencyState,
    timelineStart,
    timelineEnd,
    activeFilterIds,
    filters,
    optionCheckedIds,
    sortedVerticalSections,
    sortedHorizontalSections,
    getSectionHeight,
    initiativesBySection,
    connectedConnectionIds,
    isIsolationActive,
    enhancedInitiatives,
  ]);

  if (!dependencyState.isDependencyMode || arrows.length === 0) {
    return null;
  }

  return (
    <DependencyArrowsContainer>
      <svg
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      >
        <defs>
          <marker
            id="dependency-arrowhead"
            markerWidth="12"
            markerHeight="12"
            refX="11"
            refY="6"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path d="M 0 0 L 12 6 L 0 12 z" fill="#6e6e72" />
          </marker>
        </defs>

        {arrows.map(arrow => {
          const containerWidth = 2000 * zoomMultiplier;
          const fromX = (arrow.from.x / 100) * containerWidth;
          const toX = (arrow.to.x / 100) * containerWidth;
          const fromY = arrow.from.y;
          const toY = arrow.to.y;

          let path;
          const dy = toY - fromY;

          if (Math.abs(dy) < 5) {
            // Same row - straight line
            path = `M ${fromX} ${fromY} L ${toX} ${toY}`;
          } else {
            // Different rows - curve LEFT around target
            const extendLeft = Math.min(toX - 25, fromX - 25); // Go LEFT of the target

            // Create path with straight lines and rounded corners
            const cornerRadius = 10; // Radius for rounded corners

            path = `
              M ${fromX} ${fromY}
              L ${extendLeft + cornerRadius} ${fromY}
              Q ${extendLeft} ${fromY}, ${extendLeft} ${
              fromY + Math.sign(toY - fromY) * cornerRadius
            }
              L ${extendLeft} ${toY - Math.sign(toY - fromY) * cornerRadius}
              Q ${extendLeft} ${toY}, ${extendLeft + cornerRadius} ${toY}
              L ${toX} ${toY}
            `;
          }

          return (
            <g key={arrow.key}>
              <path
                d={path}
                stroke="#6e6e72"
                strokeWidth="2"
                fill="none"
                strokeDasharray="4 4"
                strokeLinecap="round"
                markerEnd="url(#dependency-arrowhead)"
                opacity={arrow.opacity}
              />
            </g>
          );
        })}
      </svg>
    </DependencyArrowsContainer>
  );
};

const DependencyArrowsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

export { GanttDependencyArrows };
